/**
 * FAQ accordion: interpolated height on open/close (~600ms), exclusive siblings included.
 * Native <details> toggle is cancelled so the UA cannot snap the panel.
 */
const DURATION_FALLBACK_MS = 600;
const EASING_FALLBACK = 'cubic-bezier(0.22, 1, 0.36, 1)';

const enhancedItems = new WeakSet();
const boundSearchRoots = new WeakSet();
/** @type {WeakMap<HTMLDetailsElement, { animation: Animation, cancelled: boolean }>} */
const panelMotion = new WeakMap();
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * @param {HTMLDetailsElement} details
 * @returns {HTMLElement | null}
 */
function panelOf(details) {
  return details.querySelector('.du-faq__a');
}

/**
 * @param {Element} el
 * @returns {{ duration: number, easing: string }}
 */
function motionConfig(el) {
  const styles = getComputedStyle(el);
  const durationRaw = styles.getPropertyValue('--du-faq-duration').trim();
  const easing = styles.getPropertyValue('--du-faq-ease').trim() || EASING_FALLBACK;
  let duration = DURATION_FALLBACK_MS;

  if (durationRaw) {
    duration = durationRaw.endsWith('ms') ? parseFloat(durationRaw) : parseFloat(durationRaw) * 1000;
  }

  if (!Number.isFinite(duration) || duration < 0) {
    duration = DURATION_FALLBACK_MS;
  }

  return { duration, easing };
}

/**
 * @param {HTMLDetailsElement} details
 * @param {boolean} open
 */
function setDetailsOpen(details, open) {
  if (details.open === open) return;
  details.dataset.duFaqProgrammatic = 'true';
  try {
    details.open = open;
  } finally {
    delete details.dataset.duFaqProgrammatic;
  }
}

/**
 * @param {HTMLDetailsElement} details
 */
function groupNameOf(details) {
  return details.getAttribute('data-du-faq-group') || details.getAttribute('name') || '';
}

/**
 * @param {HTMLDetailsElement} details
 */
function releasePanel(details) {
  const panel = panelOf(details);
  if (panel) {
    panel.style.height = '';
    panel.style.overflow = '';
  }
  delete details.dataset.duFaqBusy;
  delete details.dataset.duFaqMode;
}

/**
 * @param {HTMLDetailsElement} details
 */
function cancelPanelMotion(details) {
  const entry = panelMotion.get(details);
  if (!entry) return;
  entry.cancelled = true;
  entry.animation.cancel();
  panelMotion.delete(details);
}

/**
 * @param {HTMLDetailsElement} details
 * @param {HTMLElement} panel
 * @param {number} from
 * @param {number} to
 * @param {() => void} onDone
 */
function interpolateHeight(details, panel, from, to, onDone) {
  cancelPanelMotion(details);

  const { duration, easing } = motionConfig(details);

  panel.style.overflow = 'hidden';
  panel.style.height = `${from}px`;

  if (reducedMotion.matches || duration < 20 || Math.abs(from - to) < 0.5) {
    panel.style.height = `${to}px`;
    onDone();
    return;
  }

  panel.getBoundingClientRect();

  const animation = panel.animate([{ height: `${from}px` }, { height: `${to}px` }], {
    duration,
    easing,
    fill: 'forwards',
  });

  const entry = { animation, cancelled: false };
  panelMotion.set(details, entry);

  animation.finished
    .then(() => {
      if (entry.cancelled) return;
      panel.style.height = `${to}px`;
      animation.cancel();
      panelMotion.delete(details);
      onDone();
    })
    .catch(() => {});
}

/**
 * @param {HTMLDetailsElement} details
 * @param {'open' | 'close'} mode
 */
function animatePanel(details, mode) {
  const panel = panelOf(details);

  if (!panel) {
    setDetailsOpen(details, mode === 'open');
    releasePanel(details);
    return;
  }

  const wasClosing = details.dataset.duFaqMode === 'close';
  const wasOpen = details.open;
  const measured = panel.getBoundingClientRect().height;
  const from = mode === 'open' && !wasOpen && !wasClosing ? 0 : measured;

  details.dataset.duFaqBusy = 'true';
  details.dataset.duFaqMode = mode;

  if (mode === 'open') {
    setDetailsOpen(details, true);
    const previousHeight = panel.style.height;
    panel.style.height = 'auto';
    const to = panel.scrollHeight;
    panel.style.height = previousHeight;

    interpolateHeight(details, panel, from, to, () => {
      releasePanel(details);
    });
    return;
  }

  interpolateHeight(details, panel, from, 0, () => {
    setDetailsOpen(details, false);
    releasePanel(details);
  });
}

/**
 * @param {HTMLDetailsElement} details
 */
function closeExclusiveSiblings(details) {
  const group = groupNameOf(details);
  const list = details.closest('.du-faq');
  if (!group || !list) return;

  list.querySelectorAll('details.du-faq__item').forEach((other) => {
    if (!(other instanceof HTMLDetailsElement) || other === details) return;
    if (groupNameOf(other) !== group) return;

    const closing = other.dataset.duFaqMode === 'close';
    const showing = other.open || other.dataset.duFaqMode === 'open';
    if (showing && !closing) {
      animatePanel(other, 'close');
    }
  });
}

/**
 * @param {HTMLDetailsElement} details
 * @returns {boolean}
 */
function isShowing(details) {
  return details.dataset.duFaqMode === 'open' || (details.open && details.dataset.duFaqMode !== 'close');
}

/**
 * @param {HTMLDetailsElement} details
 */
function enhanceItem(details) {
  if (enhancedItems.has(details)) return;
  const summary = details.querySelector('summary');
  if (!(summary instanceof HTMLElement)) return;

  enhancedItems.add(details);
  details.classList.add('is-du-faq-js');

  if (details.hasAttribute('name')) {
    details.setAttribute('data-du-faq-group', details.getAttribute('name') || '');
    details.removeAttribute('name');
  }

  details.addEventListener('beforetoggle', (event) => {
    if (details.dataset.duFaqProgrammatic === 'true') return;
    event.preventDefault();
  });

  summary.addEventListener('click', (event) => {
    event.preventDefault();

    if (isShowing(details)) {
      animatePanel(details, 'close');
      return;
    }

    closeExclusiveSiblings(details);
    animatePanel(details, 'open');
  });
}

/**
 * @param {ParentNode} root
 */
function bindSearch(root) {
  if (!(root instanceof Element)) return;
  if (boundSearchRoots.has(root)) return;

  const input = root.querySelector('[data-du-faq-search]');
  if (!(input instanceof HTMLInputElement)) return;

  boundSearchRoots.add(root);
  const items = root.querySelectorAll('[data-du-faq-item]');
  const empty = root.querySelector('[data-du-faq-empty]');

  const applyFilter = () => {
    const query = input.value.trim().toLowerCase();
    let visible = 0;

    items.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      const haystack = (item.textContent || '').toLowerCase();
      const match = query === '' || haystack.includes(query);
      item.classList.toggle('is-du-faq-hidden', !match);
      if (match) visible += 1;
    });

    root.querySelectorAll('.du-faq').forEach((list) => {
      if (!(list instanceof HTMLElement)) return;
      const anyVisible = [...list.querySelectorAll('.du-faq__item')].some(
        (item) => item instanceof HTMLElement && !item.classList.contains('is-du-faq-hidden')
      );
      list.hidden = !anyVisible;
      const heading = list.previousElementSibling;
      if (heading instanceof HTMLElement && /^H[2-4]$/.test(heading.tagName)) {
        heading.hidden = !anyVisible;
      }
    });

    if (empty instanceof HTMLElement) {
      empty.hidden = visible > 0;
    }
  };

  input.addEventListener('input', applyFilter);
}

/**
 * @param {ParentNode} [scope]
 */
function ensureFaqPageSearch(scope = document) {
  if (!/^\/pages\/faq\/?$/.test(window.location.pathname)) return;
  if (!(scope instanceof Document) && !(scope instanceof Element)) return;
  if (scope.querySelector('[data-du-faq-search]')) return;

  const firstList = scope.querySelector('.du-faq');
  if (!(firstList instanceof HTMLElement)) return;

  const host = firstList.closest('.rte, .du-prose, main') || firstList.parentElement;
  if (!(host instanceof HTMLElement)) return;

  host.setAttribute('data-du-faq-root', '');
  host.querySelectorAll('details.du-faq__item').forEach((item) => {
    item.setAttribute('data-du-faq-item', '');
  });

  const wrap = document.createElement('div');
  wrap.className = 'du-faq__search-wrap';
  wrap.innerHTML =
    '<label class="visually-hidden" for="DuFaqPageSearch">Szukaj pytania</label>' +
    '<input id="DuFaqPageSearch" class="du-faq__search" type="search" autocomplete="off" placeholder="Szukaj pytania…" data-du-faq-search>' +
    '<p class="du-faq__empty" data-du-faq-empty hidden>Brak pytań pasujących do tego hasła.</p>';
  firstList.parentElement?.insertBefore(wrap, firstList);
}

/**
 * @param {ParentNode} [scope]
 */
function initFaq(scope = document) {
  ensureFaqPageSearch(scope);
  scope.querySelectorAll('details.du-faq__item').forEach((item) => {
    if (item instanceof HTMLDetailsElement) enhanceItem(item);
  });
  scope.querySelectorAll('[data-du-faq-root]').forEach(bindSearch);
}

initFaq();

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) initFaq(event.target);
});
